import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Coins, Landmark, Minus, PartyPopper, Plus, Receipt } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { BankCard, checkReceiptFile, ReceiptDropzone } from '@/components/coins';
import { PageHeader } from '@/components/layout/AppShell';
import { Page, Rise } from '@/components/motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Input, Textarea } from '@/components/ui/input';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/misc';
import { coinsApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import type { Purchase } from '@/lib/api/types';
import { useMe } from '@/lib/auth';
import { cn, formatMoney } from '@/lib/utils';

const STEPS = [
  { label: 'Choose coins', icon: Coins },
  { label: 'Transfer', icon: Landmark },
  { label: 'Upload receipt', icon: Receipt },
];

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function Stepper({ step }: { step: number }) {
  return (
    <div className="mb-6 flex items-center gap-2">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.label}>
          <div className="flex items-center gap-2">
            <motion.span
              animate={{ scale: i === step ? 1.08 : 1 }}
              className={cn(
                'flex size-9 items-center justify-center rounded-full text-sm font-bold transition-colors',
                i < step && 'bg-emerald-500 text-white',
                i === step && 'bg-brand-gradient text-primary-foreground shadow-glow',
                i > step && 'bg-secondary text-muted-foreground',
              )}
            >
              {i < step ? <CheckCircle2 className="size-4" /> : <s.icon className="size-4" />}
            </motion.span>
            <span className={cn('hidden text-sm font-semibold sm:inline', i > step && 'text-muted-foreground')}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className="relative h-0.5 flex-1 overflow-hidden rounded-full bg-border">
              <motion.div className="absolute inset-y-0 left-0 bg-emerald-500" initial={false} animate={{ width: i < step ? '100%' : '0%' }} />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function Success({ purchase, onAgain }: { purchase: Purchase; onAgain: () => void }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center gap-4 py-8 text-center">
      <motion.span
        initial={{ rotate: -20, scale: 0 }}
        animate={{ rotate: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 12 }}
        className="flex size-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
      >
        <PartyPopper className="size-8" />
      </motion.span>
      <div>
        <h2 className="text-xl font-bold">Receipt sent!</h2>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
          We'll review your payment of {formatMoney(purchase.amount, purchase.currency)} and add {purchase.coins_requested} coins to your balance once it's approved.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        <Button asChild variant="brand">
          <Link to="/coins/purchases">Track my purchase</Link>
        </Button>
        <Button variant="outline" onClick={onAgain}>
          Buy more
        </Button>
      </div>
    </motion.div>
  );
}

export function BuyCoinsPage() {
  const me = useMe();
  const qc = useQueryClient();
  const pricing = useQuery({ queryKey: ['pricing'], queryFn: coinsApi.pricing });
  const rules = pricing.data?.rules;
  const accounts = pricing.data?.bank_accounts ?? [];

  const [step, setStep] = React.useState(0);
  const [dir, setDir] = React.useState(1);
  const [coins, setCoins] = React.useState<number | null>(null);
  const [accountId, setAccountId] = React.useState<string | null>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | undefined>();
  const [ref, setRef] = React.useState('');
  const [note, setNote] = React.useState('');
  const [done, setDone] = React.useState<Purchase | null>(null);

  const min = rules?.min_purchase_coins ?? 1;
  const count = coins ?? Math.max(min, rules?.price_coins ?? min);
  const debounced = useDebounced(count, 250);
  const quote = useQuery({
    queryKey: ['quote', debounced],
    queryFn: ({ signal }) => coinsApi.quote(debounced, signal),
    enabled: !!rules && debounced >= min,
    placeholderData: keepPreviousData,
  });

  React.useEffect(() => {
    if (!accountId && accounts[0]) setAccountId(accounts[0].id);
  }, [accounts, accountId]);

  const account = accounts.find((a) => a.id === accountId) ?? null;
  const go = (n: number) => {
    setDir(n > step ? 1 : -1);
    setStep(n);
  };

  const submit = useMutation({
    mutationFn: () => coinsApi.createPurchase({ coins: count, bank_account_id: accountId!, receipt: file!, transaction_ref: ref.trim() || undefined, note: note.trim() || undefined }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['purchases'] });
      setDone(p);
    },
    onError: (err) => {
      if (isApiError(err, 413) || isApiError(err, 415)) setFileError(err.message);
      else if (isApiError(err, 422)) {
        const fe = err.fieldErrors;
        if (fe.receipt) setFileError(fe.receipt);
        toast.error(err.message);
      } else toast.error(userMessage(err, 'Upload failed'));
    },
  });

  if (pricing.isPending) return <Skeleton className="h-96 w-full rounded-2xl" />;
  if (pricing.isError || !rules) return <ErrorState error={pricing.error} onRetry={() => pricing.refetch()} />;

  const presets = [min, min * 2, min * 5, min * 10].filter((v, i, a) => a.indexOf(v) === i);
  const checksFor = rules.check_cost_coins > 0 ? Math.floor(count / rules.check_cost_coins) : null;

  return (
    <Page>
      <PageHeader
        eyebrow="Coins"
        title="Buy coins"
        description={`Pay ${formatMoney(rules.price_amount, rules.currency)} for ${rules.price_coins} coins (${formatMoney(rules.price_per_coin, rules.currency)} each). Each check costs ${rules.check_cost_coins}.`}
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <Rise>
          <Card className="overflow-hidden">
            <CardContent className="pt-6 sm:pt-6">
              {done ? (
                <Success
                  purchase={done}
                  onAgain={() => {
                    setDone(null);
                    setFile(null);
                    setRef('');
                    setNote('');
                    go(0);
                  }}
                />
              ) : accounts.length === 0 ? (
                <EmptyState icon={<Landmark />} title="No payment accounts yet" description="Buying coins isn't available right now. Please check back later." />
              ) : (
                <>
                  <Stepper step={step} />
                  <AnimatePresence mode="wait" custom={dir}>
                    <motion.div
                      key={step}
                      custom={dir}
                      initial={{ opacity: 0, x: dir * 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: dir * -30 }}
                      transition={{ duration: 0.22 }}
                    >
                      {step === 0 && (
                        <div className="space-y-6">
                          <div className="flex flex-wrap gap-2">
                            {presets.map((p) => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => setCoins(p)}
                                className={cn(
                                  'rounded-full border px-4 py-1.5 text-sm font-semibold transition-all',
                                  count === p ? 'border-primary bg-primary text-primary-foreground shadow-glow' : 'hover:border-primary/50',
                                )}
                              >
                                {p} coins
                              </button>
                            ))}
                          </div>
                          <div className="flex items-center justify-center gap-4">
                            <Button type="button" variant="outline" size="icon" className="size-12 rounded-full" disabled={count <= min} onClick={() => setCoins(Math.max(min, count - 1))}>
                              <Minus />
                            </Button>
                            <div className="w-36 text-center">
                              <input
                                type="number"
                                min={min}
                                value={count}
                                onChange={(e) => setCoins(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                                onBlur={() => setCoins(Math.max(min, count))}
                                className="w-full bg-transparent text-center text-5xl font-black tracking-tight outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                                aria-label="Number of coins"
                              />
                              <p className="text-sm text-muted-foreground">coins</p>
                            </div>
                            <Button type="button" variant="outline" size="icon" className="size-12 rounded-full" onClick={() => setCoins(count + 1)}>
                              <Plus />
                            </Button>
                          </div>
                          <div className="rounded-2xl bg-gradient-to-br from-brand-sun/10 to-brand-navy/10 p-5 text-center">
                            <p className="text-sm text-muted-foreground">You'll transfer</p>
                            <motion.p key={quote.data?.amount} initial={{ opacity: 0.4, y: 4 }} animate={{ opacity: 1, y: 0 }} className={cn('text-3xl font-black tracking-tight', quote.isFetching && 'opacity-60')}>
                              {count < min ? '—' : quote.data ? formatMoney(quote.data.amount, quote.data.currency) : '…'}
                            </motion.p>
                            {checksFor !== null && count >= min && <p className="mt-1 text-xs text-muted-foreground">Enough for {checksFor} check{checksFor === 1 ? '' : 's'}</p>}
                            {count < min && <p className="mt-1 text-xs font-medium text-destructive">The minimum purchase is {min} coins.</p>}
                          </div>
                          <Button className="group w-full" size="lg" variant="brand" disabled={count < min || !quote.data} onClick={() => go(1)}>
                            Continue <ArrowRight className="transition-transform group-hover:translate-x-1" />
                          </Button>
                        </div>
                      )}

                      {step === 1 && (
                        <div className="space-y-5">
                          <p className="text-sm text-muted-foreground">
                            Transfer <span className="font-bold text-foreground">{quote.data ? formatMoney(quote.data.amount, quote.data.currency) : ''}</span> to one of these accounts, then keep the receipt.
                          </p>
                          <div role="radiogroup" className="grid gap-3 sm:grid-cols-2">
                            {accounts.map((a) => (
                              <BankCard key={a.id} account={a} selected={a.id === accountId} onSelect={() => setAccountId(a.id)} />
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="lg" onClick={() => go(0)}>
                              <ArrowLeft /> Back
                            </Button>
                            <Button className="group flex-1" size="lg" variant="brand" disabled={!account} onClick={() => go(2)}>
                              I've made the transfer <ArrowRight className="transition-transform group-hover:translate-x-1" />
                            </Button>
                          </div>
                        </div>
                      )}

                      {step === 2 && (
                        <form
                          className="space-y-5"
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (!file) return setFileError('Attach the transfer receipt.');
                            submit.mutate();
                          }}
                        >
                          <ReceiptDropzone
                            file={file}
                            error={fileError}
                            onFile={(f) => {
                              setFile(f);
                              setFileError(f ? (checkReceiptFile(f) ?? undefined) : undefined);
                            }}
                          />
                          <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Bank reference (optional)" htmlFor="ref" hint="e.g. FT2610130001">
                              <Input id="ref" value={ref} maxLength={100} onChange={(e) => setRef(e.target.value)} />
                            </Field>
                            <Field label="Note (optional)" htmlFor="note">
                              <Textarea id="note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} className="min-h-11" rows={1} />
                            </Field>
                          </div>
                          <div className="flex gap-2">
                            <Button type="button" variant="outline" size="lg" onClick={() => go(1)}>
                              <ArrowLeft /> Back
                            </Button>
                            <Button type="submit" className="flex-1" size="lg" variant="brand" loading={submit.isPending} disabled={!file || !!fileError}>
                              Submit for review
                            </Button>
                          </div>
                        </form>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </>
              )}
            </CardContent>
          </Card>
        </Rise>

        <Rise className="space-y-4">
          <Card className="relative overflow-hidden p-5">
            <div className="absolute -right-8 -top-8 size-28 rounded-full bg-amber-400/25 blur-2xl" />
            <p className="relative text-sm text-muted-foreground">Current balance</p>
            <p className="relative mt-1 flex items-center gap-2 text-3xl font-black">
              <Coins className="size-6 text-amber-500" /> {me.coin_balance}
            </p>
          </Card>
          <Card className="p-5 text-sm">
            <p className="font-semibold">Order summary</p>
            <dl className="mt-3 space-y-2 text-muted-foreground">
              <div className="flex justify-between">
                <dt>Coins</dt>
                <dd className="font-semibold text-foreground">{count}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Price per coin</dt>
                <dd>{formatMoney(rules.price_per_coin, rules.currency)}</dd>
              </div>
              <div className="flex justify-between border-t pt-2">
                <dt className="font-semibold text-foreground">Total</dt>
                <dd className="font-bold text-foreground">{quote.data && count >= min ? formatMoney(quote.data.amount, quote.data.currency) : '—'}</dd>
              </div>
              {account && step > 0 && (
                <div className="flex justify-between gap-3 pt-1">
                  <dt>Pay to</dt>
                  <dd className="truncate text-right">{account.bank_name}</dd>
                </div>
              )}
            </dl>
            <p className="mt-4 rounded-lg bg-secondary/70 px-3 py-2 text-xs text-muted-foreground">An admin checks every receipt. Coins appear in your balance once approved.</p>
          </Card>
        </Rise>
      </div>
    </Page>
  );
}
